<template>
  <div
    v-if="ready"
    id="app"
    class="tool-container px-md-4"
  >
    <div
      id="questionnaire-panels"
      :class="[ 'panel-container pb-xs-0', stage?.classes ]"
    >
      <component
        :is="stage.type"
        :stage="stage"
        @action="handleAction"
      ></component>

      <div
        id="footnote"
        class="container mt-xs-4 px-md-0 mb-xs-40"
      >
        <p v-if="conditionals('key')" id="key" class="smaller fg-sma-black-50" v-html="$getString('GLOBAL_ABBREVIATIONS')"></p>
        <p v-if="conditionals('footnote')" class="smaller fg-sma-black-75" v-html="$getString('GLOBAL_FOOTNOTE')"></p>
        <p v-if="conditionals('approval-code')" id="approval-code" class="smaller text-weight-medium fg-sma-black">
          <span v-html="$getString('REG_APPROVAL_CODE')"></span> | <span v-html="$getString('REG_APPROVAL_DATE')"></span>
        </p>
        <p v-if="conditionals('footnote')" class="smaller text-weight-medium fg-sma-black" v-html="$getString('REG_COPYRIGHT')"></p>
      </div>

      <div class="close" @click="onReturn"></div>
    </div>

    <ToolFooter v-if="stage.footer" :progress="progress / pages" @action="handleAction" />

    <div
      :class="[
        'popup-container p-xs-4',
        { active: popupState },
        { 'opacity-7': popupState && popup.type === 'share' },
      ]"
      @click.stop="popupState = false"
    >
      <div :class="[ 'popup-inner pt-xs-40', { active: popupState && popup.type === 'symptom' } ]">
        <h1 class="popup-heading" v-html="popup?.heading"></h1>
        <div class="bottom-hr bg-sma-turquoise mb-xs-4"></div>
        <ul class="popup-list">
          <li v-for="(item, index) in popup?.items" :key="index" v-html="item"></li>
        </ul>
        <div class="close" @click.stop="popupState = false"></div>
      </div>

      <div :class="[ 'popup-inner pt-xs-40', { active: popupState && popup.type === 'further' } ]">
        <h1 class="popup-heading" v-html="$getString('POPUP_FURTHER_INFO_HEADING')"></h1>
        <div class="bottom-hr bg-sma-turquoise mb-xs-4"></div>
        <h2 class="mb-xs-3" v-html="$getString('POPUP_FURTHER_INFO_LINKS_TEXT')"></h2>

        <div class="questionnaire-summary mb-xs-4 pb-xs-2">
          <a class="questionnaire-link bg-sma-white fg-sma-fuchsia text-uppercase cursor-pointer" v-html="$getString('POPUP_FURTHER_INFO_ABOUT_SMA')" @click.stop="logLink('/what-is-spinal-muscular-atrophy', 'Internal_link_click', 'Further_info_about_sma')"></a>
          <a class="questionnaire-link bg-sma-white fg-sma-fuchsia text-uppercase cursor-pointer" v-html="$getString('POPUP_FURTHER_INFO_COMMON_FAQS')" @click.stop="logLink('/faq', 'Internal_link_click', 'Further_info_common_faqs')"></a>
        </div>
        
        <div class="bg-sma-turquoise-10 px-xs-4 py-xs-4 mt-xs-4 mb-xs-4">
          <h3 class="mb-xs-2" v-html="$getString('POPUP_FURTHER_INFO_ADDITIONAL_SUPPORT')"></h3>
          <p class="small fg-sma-black-50 mb-xs-4" v-html="$getString('POPUP_FURTHER_INFO_PATIENT_ASSOCIATION_GROUPS')"></p>

          <div class="buttons">
            <a
              class="tool-button next text-uppercase"
              v-html="$getString('POPUP_FURTHER_INFO_FIND_PAGS')"
              @click.stop="logLink('/community', 'Internal_link_click', 'Further_info_find_local_sma_pags')"
            ></a>
          </div>
        </div>

        <p class="smaller fg-sma-black-75" v-html="$getString('POPUP_FURTHER_INFO_ABBREVIATIONS')"></p>

        <div class="close" @click.stop="closePopup('Further_info_btn_close')"></div>
      </div>

      <div :class="[ 'popup-inner pt-xs-40', { active: popupState && popup.type === 'saved' } ]">
        <h1 class="popup-heading" v-html="$getString('POPUP_SAVE_SAVED')"></h1>
        <div class="bottom-hr bg-sma-turquoise mb-xs-4"></div>
        <p class="fg-sma-black mb-xs-3" v-html="$getString('POPUP_SAVE_PROGRESS')"></p>

        <div class="buttons">
          <div class="tool-button next text-uppercase" @click.stop="popupState = false" v-html="$getString('POPUP_BUTTON_RETURN')"></div>
        </div>
        <div class="restart-buttons container text-centered mt-xs-4">
          <div class="questionnaire-link icon-leave bg-sma-white fg-sma-fuchsia-50 text-uppercase" @click="onReturn" v-html="$getString('POPUP_SAVE_LEAVE_QUESTIONNAIRE')"></div>
        </div>

        <div class="close" @click.stop="popupState = false"></div>
      </div>

      <div :class="[ 'popup-inner popup-inner-tray', { active: popupState && popup.type === 'share' } ]">
        <h1 class="popup-heading text-xs-center" v-html="$getString('POPUP_SHARE_RESULTS')"></h1>
        <div class="buttons">
          <div class="app-button app-button-whatsapp d-xs-block d-lg-none" @click.stop="sendMessage('whatsapp')" v-html="$getString('POPUP_SHARE_WHATSAPP')"></div>
          <div class="app-button app-button-email" @click.stop="sendMessage('email')" v-html="$getString('POPUP_SHARE_EMAIL')"></div>
        </div>
        <div class="close" @click.stop="closePopup('Summary_of_results_share_close_btn')"></div>
      </div>
    </div>
  </div>
</template>

<script>
import ToolFooter from './components/ToolFooter.vue'
import StageAddSymptoms from './components/StageAddSymptoms.vue'
import StageAgeSelect from './components/StageAgeSelect.vue'
import StageIntroduction from './components/StageIntroduction.vue'
import StageQuestions from './components/StageQuestions.vue'
import StageSliders from './components/StageSliders.vue'
import StageSummary from './components/StageSummary.vue'
import StageSummaryAlt from './components/StageSummaryAlt.vue'
import StageSymptoms from './components/StageSymptoms.vue'

export default {
  name: 'App',
  components: {
    ToolFooter,
    StageAddSymptoms,
    StageAgeSelect,
    StageIntroduction,
    StageQuestions,
    StageSliders,
    StageSummary,
    StageSummaryAlt,
    StageSymptoms,
  },
  data() {
    return {
      backlink: '',
      progress: 0,
      pages: 0,
      ready: false,
      storageId: '',
      stages: [],
      stageId: 'introduction',
      popup: {},
      popupState: false,

      handlers: {
        'goto-stage': this.onGotoStage,
        'increment-page': this.onIncrementPage,
        'decrement-page': this.onDecrementPage,
        'complete-progress': this.onCompleteProgress,
        'set-pages': this.onSetPages,
        'open-popup': this.onOpenPopup,
        'store-results-url': this.storeResultsUrl,
        'home': this.onHome,
        'save': this.onSave,
        'return': this.onReturn,
        'restart': this.onRestart,
        'reset': this.onReset,
        'log-analytics': this.logAnalytics,
      },

      providers: {
        'whatsapp': { body: 'SHARE_BODY', url: 'https://api.whatsapp.com/send?text=[body]', proc: body => encodeURIComponent(body) },
        'email': { body: 'SHARE_BODY', url: 'mailto:?subject=[subject]&body=[body]', proc: body => body },
      }
    }
  },
  computed: {
    stage() {
      return this.stages[this.stageId];
    },
  },
  methods: {
    // Action Hander
    handleAction(event) {
      if (event.type in this.handlers) {
        this.handlers[event.type](event.data);
      }
    },
    onGotoStage(id) {
      if (id in this.stages) {
        this.$store.commit('setQuestionLabel', '');
        this.stageId = id;
      }
    },
    onIncrementPage() {
      this.progress += 1;
    },
    onDecrementPage() {
      this.progress -= 1;
    },
    onCompleteProgress() {
      this.progress = this.$store.getters.pages;
    },
    onSetPages(count) {
      this.$store.commit('SetPages', count);
      this.pages = this.$store.getters.pages;
    },
    onReset() {
      this.clearStorage();
    },
    onOpenPopup(popup) {
      this.popup = popup;
      this.popupState = true;
    },
    closePopup(analyticsValue) {
      this.logAnalytics({
        "category": 'Internal_link_click',
        "action": '/',
        "label": analyticsValue,
      });
      this.popupState = false;
    },
    logLink(url, category, analyticsValue) {
      this.logAnalytics({
        "category": category,
        "action": '/',
        "label": analyticsValue,
      });
      window.open(this.homelink + url, '_blank');
    },
    storeResultsUrl(url) {
      this.$store.commit('SetData', { key: 'results-url', value: url });
    },
    onSave() {
      this.saveData();
      const resultsUrl = this.$store.getters.dataKey('results-url');
      if (!resultsUrl) {
        window.localStorage.removeItem(this.storageId + '_LINK');
      } else {
        const links = JSON.stringify(resultsUrl);
        window.localStorage.setItem(this.storageId + '_LINK', links);
      }
    },
    onReturn() {
      this.logAnalytics({
        "category": 'Internal_link_click',
        "action": '/',
        "label": '{page_label}_Questionnaire_close',
      });
      const url = this.$store.getters.link('backlink');
      window.location.href = url;
    },
    onHome() {
      this.logAnalytics({
        "category": 'Internal_link_click',
        "action": '/',
        "label": '{page_label}_Home_btn_progress',
      });
      this.onRestart();
    },
    onRestart() {
      this.clearStorage();
      this.onGotoStage('introduction');
    },
    clearStorage() {
      this.clearData();
      this.saveData();
      window.localStorage.removeItem(this.storageId + '_LINK');
      this.resetProgress();
      this.resetQuestion();
    },

    conditionals(id) {
      return this.stage.conditionals.indexOf(id) > -1;
    },

    // Initialisation

    importConfig(config) {
      this.stages = config['stages'];
      const analytics = config['analytics'];
      analytics['locale'] = document.body.getAttribute('data-lang');
      this.$store.commit('SetAnalytics', analytics);
      this.$store.commit('SetStrings', config['strings']);
      this.$store.commit('SetAgeGroups', config['age-groups']);
      this.loadData();
      this.resetQuestion();

    },
    resetProgress() {
      this.$store.commit('SetPages', 0);
      this.progress = 0;
      this.pages = this.$store.getters.pages;
      this.resetQuestion();
    },
    resetQuestion() {
      this.$store.commit('SetData', { key: 'question', value: -1 });
      this.saveData();
    },

    // Share

    getMessageUrl(provider, link) {
      let subject = this.$getString('SHARE_SUBJECT');
      let body = this.$getString(provider.body);
      let url = provider.url;

      body = body.replace('[link]', link);
      body = provider.proc(body);
      url = url.replace('[subject]', subject);
      url = url.replace('[body]', body);

      return url;
    },

    sendMessage(type) {
      const provider = this.providers[type];
      const link = this.$store.getters.dataKey('results-url');
      const url = this.getMessageUrl(provider, link.download);
      
      this.$emit('action', { type: 'log-analytics', data: {
        "category": 'Internal_link_click',
        "action": '/',
        "label": `Summary_of_results_share_${type}_btn`,
      } });
      
      window.open(url, '_blank');
    },

    // Data

    getDefaultData() {
      return {
        "age": -1,
        "symptoms": [],
        "questions": [],
        "question": -1,
        "sliders": {},
        "addedSymptoms": false,
        "saved": false
      };
    },
    clearData() {
      window.localStorage.removeItem(this.storageId);
      let data = this.getDefaultData();
      this.$store.commit('InitialiseData', data);
    },
    loadData() {
      const savedData = window.localStorage.getItem(this.storageId);
      let data = this.getDefaultData();
      if (savedData !== null) data = JSON.parse(savedData);
      this.$store.commit('InitialiseData', data);
      this.ready = true;
    },
    saveData() {
      const data = this.$store.getters.data;
      window.localStorage.setItem(this.storageId, JSON.stringify(data));
    },
    storeLink(key) {
      const value = document.body.getAttribute(`data-${key}`);
      document.body.removeAttribute(`data-${key}`);
      this.$store.commit('SetLink', { key, value });
    },

    // Analytics

    getAnalyticsTokens() {
      return {
        'page_label': this.stage['page_label'],
        'page_name': this.$store.getters.analytics['page_name'],
        'question': this.$store.getters.questionLabel,
        'age': this.$store.getters.ageRange,
      }
    },

    getAnalyticsEventString(template, tokens) {
      let buffer = template;
      for (let key in tokens) {
        buffer = buffer.replaceAll(`{${key}}`, tokens[key]);
      }
      return buffer;
    },

    logAnalytics(data) {
      const tokens = this.getAnalyticsTokens();
      const analyticsData = {
        "page_name": this.getAnalyticsEventString(this.stage['page_name'], tokens),
        "event_category": data.category,
        "event_action": data.action,
        "event_label": this.getAnalyticsEventString(data.label, tokens),
      };

      if (window.gtag)
        window.gtag('event', analyticsData['event_category'], analyticsData);
      else
        console.log(analyticsData);
    },

  },
  created() {
    const region = document.body.getAttribute('data-region');
    this.storageId = `SIGNSOFSMA_CAREGIVER_TOOL_${region}`;
    
    this.storeLink('homelink');
    this.storeLink('backlink');
    this.storeLink('results');
    this.storeLink('results-download');
    this.backlink = this.$store.getters.link('backlink');
    this.homelink = this.$store.getters.link('homelink');

    this.pages = this.$store.getters.pages;

    fetch(`/files/data/cgtool-${region}.json`)
      .then(response => response.json())
      .then(data => this.importConfig.call(this, data));
  },
}
</script>
